import { inject, Injectable } from '@angular/core';

import { API_URLS, WebServiceProvider } from '@pinnakl/core/web-services';
import { AssetType, Security, SecurityFromApi } from '@pinnakl/shared/types';
import moment from 'moment';

export interface OptionLookupChainExpiration {
  callStrikes: number[];
  putStrikes: number[];
  expirationDate: string;
}

export interface OptionLookupChainResponse {
  expirations: OptionLookupChainExpiration[];
}

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private readonly wsp = inject(WebServiceProvider);
  private readonly apiUrls = inject(API_URLS);

  private _assetTypes: AssetType[];

  getCusipsByTicker(identifier: string): Promise<any> {
    return this.wsp.get({
      endpoint: this.apiUrls.idcTickerSearch,
      queryParams: {
        options: JSON.stringify({ filters: [{ key: 'ticker', type: 'EQ', value: [identifier] }] }),
      },
    });
  }

  getAssetTypes(): Promise<AssetType[]> {
    if (this._assetTypes) {
      return Promise.resolve(this._assetTypes);
    }

    return this.wsp
      .get<AssetType[]>({
        endpoint: this.apiUrls.assetTypesV3,
      })
      .then(assetTypes => {
        this._assetTypes = assetTypes;
        return this._assetTypes;
      });
  }

  async saveSecurityAutomatically(identifierType: string, identifierValue: string): Promise<Security> {
    try {
      const securityFromApi = await this.wsp.post<SecurityFromApi>({
        endpoint: this.apiUrls.securitiesV4Import,
        payload: {
          identifierType: identifierType,
          identifierValue: identifierValue,
        },
      });
      return this.formatSecurity(securityFromApi);
    } catch (e) {
      if (e.message && e.message.toLowerCase().includes('mandatory fields')) {
        throw { clientMessage: e.message };
      }
      throw e;
    }
  }

  public optionChainLookup(ticker: string, callPut: string): Promise<OptionLookupChainResponse> {
    return this.wsp.post<OptionLookupChainResponse>({
      endpoint: this.apiUrls.optionChainLookup,
      payload: {
        ticker,
        callPut,
      },
    });
  }

  private formatSecurity(entity: SecurityFromApi): Security {
    if (!entity) {
      return;
    }
    const assetTypeId = +entity.assettypeid,
      currencyId = +entity.currencyid,
      dataSourceId = +entity.datasource,
      id = +entity.id,
      multiplier = +entity.multiplier,
      organizationId = +entity.organization_id,
      securityTypeId = +entity.sectypeid,
      principalFactor = parseFloat(entity.principal_factor),
      initialMargin = parseFloat(entity.initialmargin),
      maintenanceMargin = parseFloat(entity.maintenancemargin),
      maturityMoment = moment(entity.maturity, 'MM/DD/yyyy');

    return new Security(
      entity.assettype,
      !isNaN(assetTypeId) ? assetTypeId : null,
      entity.countryofincorporation,
      entity.countryofrisk,
      +entity.countryofquotationid,
      entity.countryofquotationcode,
      entity.countryofquotationname,
      entity.countryofincorporationcode,
      entity.countryofincorporationname,
      entity.currency,
      !isNaN(currencyId) ? currencyId : null,
      entity.cusip,
      !isNaN(dataSourceId) ? dataSourceId : null,
      entity.description,
      !isNaN(id) ? id : null,
      entity.isin,
      entity.loanid,
      entity.manualpricingindicator === 'True',
      entity.moodyrating,
      !isNaN(multiplier) ? multiplier : null,
      entity.opracode,
      !isNaN(organizationId) ? organizationId : null,
      entity.organizationname,
      entity.organization_status_descr,
      entity.organization_status_id,
      entity.organizationticker,
      entity.privateindicator === 'True',
      entity.sandprating,
      entity.sector,
      entity.sectype,
      entity.sectypedescription,
      !isNaN(securityTypeId) ? securityTypeId : null,
      entity.sedol,
      entity.ticker,
      !isNaN(principalFactor) ? principalFactor : null,
      !isNaN(initialMargin) ? initialMargin : null,
      !isNaN(maintenanceMargin) ? maintenanceMargin : null,
      maturityMoment.isValid() ? maturityMoment.toDate() : null,
      entity.underlyingsecid ? +entity.underlyingsecid : null,
      +entity.sharesoutstanding,
      entity.pricingsourcename,
      entity.securitypricingid,
      +entity.securitymarketid,
      +entity.marketid,
      +entity.id,
    );
  }
}
